// src/components/pages/Pessoa/tabs/PessoaImagemField.jsx

import { useCallback, useEffect, useRef, useState } from 'react';

import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    useMediaQuery,
} from '@mui/material';

import Input from '../../../form/Input';

function mensagemErroCamera(error) {
    switch (error?.name) {
        case 'NotAllowedError':
        case 'PermissionDeniedError':
            return 'Permissão para utilizar a câmera não concedida.';

        case 'NotFoundError':
        case 'DevicesNotFoundError':
            return 'Nenhuma câmera foi encontrada neste dispositivo.';

        case 'NotReadableError':
        case 'TrackStartError':
            return 'A câmera está sendo utilizada por outro aplicativo.';

        default:
            return 'Não foi possível abrir a câmera.';
    }
}

export default function PessoaImagemField({ dados, module }) {
    const isMobile = useMediaQuery('(max-width: 900px)');

    const cameraInputRef = useRef(null);
    const videoRef = useRef(null);
    const streamRef = useRef(null);

    const [cameraOpen, setCameraOpen] = useState(false);
    const [cameraLoading, setCameraLoading] = useState(false);
    const [cameraError, setCameraError] = useState('');

    const { preview } = module.state;
    const { onFileChange, limparImagem } = module.actions;

    const pararWebcam = useCallback(() => {
        const stream = streamRef.current;

        if (stream?.getTracks) {
            stream.getTracks().forEach((track) => track.stop());
        }

        streamRef.current = null;

        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.srcObject = null;
        }
    }, []);

    const fecharWebcam = useCallback(() => {
        pararWebcam();
        setCameraOpen(false);
        setCameraLoading(false);
        setCameraError('');
    }, [pararWebcam]);

    function abrirCamera() {
        setCameraError('');

        if (isMobile) {
            if (!cameraInputRef.current) return;

            cameraInputRef.current.value = '';
            cameraInputRef.current.click();
            return;
        }

        setCameraOpen(true);
    }

    useEffect(() => {
        if (!cameraOpen || isMobile) return undefined;

        let effectAtivo = true;

        async function iniciarWebcam() {
            setCameraLoading(true);
            setCameraError('');

            try {
                if (!navigator.mediaDevices?.getUserMedia) {
                    throw Object.assign(new Error('Câmera indisponível'), { name: 'NotSupportedError' });
                }

                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: {
                        facingMode: {
                            ideal: 'user',
                        },
                        width: {
                            ideal: 1280,
                        },
                        height: {
                            ideal: 720,
                        },
                    },
                });

                if (!effectAtivo) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }

                streamRef.current = stream;

                if (!videoRef.current) {
                    throw new Error('Elemento de vídeo indisponível.');
                }

                videoRef.current.srcObject = stream;
                await videoRef.current.play();

                if (effectAtivo) {
                    setCameraLoading(false);
                }
            } catch (error) {
                if (!effectAtivo) return;

                console.error('Erro ao abrir webcam:', error);
                pararWebcam();
                setCameraLoading(false);
                setCameraError(mensagemErroCamera(error));
            }
        }

        iniciarWebcam();

        return () => {
            effectAtivo = false;
            pararWebcam();
        };
    }, [cameraOpen, isMobile, pararWebcam]);

    async function capturarFoto() {
        const video = videoRef.current;

        if (!video?.videoWidth || !video?.videoHeight) {
            setCameraError('A imagem da câmera ainda não está disponível.');
            return;
        }

        setCameraLoading(true);
        setCameraError('');

        try {
            const canvas = document.createElement('canvas');

            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            const contexto = canvas.getContext('2d');

            if (!contexto) {
                throw new Error('Não foi possível capturar a imagem.');
            }

            contexto.drawImage(video, 0, 0, canvas.width, canvas.height);

            const blob = await new Promise((resolve, reject) => {
                canvas.toBlob(
                    (resultado) => {
                        if (resultado) {
                            resolve(resultado);
                        } else {
                            reject(new Error('Não foi possível gerar a fotografia.'));
                        }
                    },
                    'image/jpeg',
                    0.9
                );
            });

            const arquivo = new File([blob], `pessoa-${Date.now()}.jpg`, {
                type: 'image/jpeg',
            });

            const transferencia = new DataTransfer();
            transferencia.items.add(arquivo);

            onFileChange({
                target: {
                    name: 'images',
                    files: transferencia.files,
                },
            });

            fecharWebcam();
        } catch (error) {
            console.error('Erro ao capturar foto:', error);
            setCameraLoading(false);
            setCameraError('Não foi possível capturar a fotografia.');
        }
    }

    return (
        <Box>
            <div>
                <Input
                    text="Fotos da Pessoa"
                    type="file"
                    name="images"
                    accept="image/*,filesystem/*" //accept=".jpg, .jpeg, .png"
                    handleOnChange={onFileChange}
                    multiple={false}
                    inputWidth="350px"
                />
            </div>

            <input
                ref={cameraInputRef}
                type="file"
                name="images"
                accept="image/*"
                capture="environment"
                onChange={onFileChange}
                style={{ display: 'none' }}
            />

            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    mt: 1,
                }}
            >
                {preview.length > 0
                    ? preview.map((image, index) => (
                          <img
                              key={`${dados.images || 'img'}-${index}`}
                              src={URL.createObjectURL(image)}
                              alt={dados.images || 'preview'}
                              style={{
                                  borderColor: 'black',
                                  border: 'solid 0.05em',
                              }}
                              width={60}
                              height={80}
                          />
                      ))
                    : dados.images &&
                      typeof dados.images === 'string' && (
                          <img
                              src={process.env.REACT_APP_API + '/imagens/' + dados.images}
                              alt={dados.uuid || 'Foto da Pessoa'}
                              style={{
                                  borderColor: 'black',
                                  border: 'solid 0.05em',
                              }}
                              width={60}
                              height={80}
                          />
                      )}

                <Button type="button" variant="contained" onClick={abrirCamera}>
                    Abrir câmera
                </Button>

                <Button type="button" variant="contained" color="inherit" onClick={limparImagem}>
                    Limpar imagem
                </Button>
            </Box>

            <Dialog open={cameraOpen} onClose={fecharWebcam} fullWidth maxWidth="sm">
                <DialogTitle>Capturar foto</DialogTitle>

                <DialogContent>
                    <Stack spacing={2}>
                        {cameraError && <Alert severity="error">{cameraError}</Alert>}

                        <Box
                            sx={{
                                position: 'relative',
                                minHeight: '300px',
                                backgroundColor: '#000',
                                borderRadius: 1,
                                overflow: 'hidden',
                            }}
                        >
                            <video
                                ref={videoRef}
                                autoPlay
                                muted
                                playsInline
                                style={{
                                    display: 'block',
                                    width: '100%',
                                    minHeight: '300px',
                                    maxHeight: '65vh',
                                    objectFit: 'contain',
                                }}
                            />

                            {cameraLoading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(0, 0, 0, 0.45)',
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            )}
                        </Box>
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button type="button" onClick={capturarFoto} disabled={cameraLoading || Boolean(cameraError)}>
                        Capturar foto
                    </Button>

                    <Button type="button" onClick={fecharWebcam}>
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
